import type {
    CatalogImportLineResult,
    CatalogImportLineStatus,
    CatalogImportMatch,
    CatalogImportProblem,
    CatalogImportResult,
} from '@/api/models';
import {
    noticeFor,
    toneFor,
    type CatalogImportNotice,
    type CatalogImportNoticeTone,
} from '@/pages/catalogImport/notices';
import type { ParsedLine } from '@/pages/catalogImport/store';
import { tetriFromDecimal } from '@/utils/money';

/**
 * What a row's status chip says: the preview's status, `excluded` for a row
 * the user left out since, or `unchecked` for a row the last check left out
 * and the user included since (no answer until the next check).
 */
export type ImportRowStatus = CatalogImportLineStatus | 'unchecked';

/** What a row's note says: one sentence and the tone it is shown in. */
export type ImportRowNote = {
    notice: CatalogImportNotice;
    tone: CatalogImportNoticeTone;
};

/**
 * One row of the Products table: a parsed line of the file joined with the
 * preview's answer for it. Read-only; built once per preview and kept per
 * row (`buildRows` reuses a row whose inputs did not change).
 */
export type ImportRow = {
    /** The Excel row number: the row's identity and the table's first column. */
    rowNumber: number;
    /** The preview's status, `excluded` as soon as the user leaves the row out, `unchecked` once included again after a check left it out. */
    status: ImportRowStatus;
    /** Whether the user left the row out (`exclusions`), checked or not: the row's Exclude / Include. */
    isExcluded: boolean;
    name: string;
    sku: string;
    /** The barcode cell as the file has it, trimmed; empty when blank (the import then takes the SKU). */
    barcode: string;
    /** The lot number cell as the file has it, trimmed (an IMEI or a serial); empty when blank. */
    lotNumber: string;
    /** How many rows make up the row's product (`groupRows`) when more than one; else `null`. */
    groupSize: number | null;
    /** The row's variation cells, `Color: Black · Storage: 128 GB`; empty when it has none. */
    attributes: string;
    quantity: number | null;
    costTetri: number | null;
    salePriceTetri: number | null;
    /** What the row does to the catalog; `null` when unmatched or excluded, as the server answers an excluded row. */
    match: CatalogImportMatch | null;
    /** The first problem, else the first warning; `null` when the row has neither. */
    note: ImportRowNote | null;
    /** The parsed line the row shows (the drawer edits it). */
    line: ParsedLine;
    /** The preview's answer for the row. */
    result: CatalogImportLineResult;
};

/**
 * What a row's one control does: leave the row out of the import, or take it
 * back. The table and the phone cards both hand them to `RowExclusionButton`.
 */
export type ImportRowExclusionHandlers = {
    onExcludeRow: (rowNumber: number) => void;
    onIncludeRow: (rowNumber: number) => void;
};

/**
 * What a row's Edit button and its note's Fix link do: open the row in the
 * drawer, as pressing the row does. The table and the phone cards hand it to
 * `RowEditButton` and `NoteCell`.
 */
export type ImportRowEditHandler = {
    onEditRow: (rowNumber: number) => void;
};

/**
 * A row whose note offers Fix: the last check found a problem on it (what
 * blocks it) and the user has not left it out. A row with only warnings
 * needs nothing, and an excluded row's note says what it had without asking
 * for it.
 */
export const isFlaggedRow = (row: ImportRow): boolean =>
    !row.isExcluded && row.result.problems.length > 0;

/** The separator between a row's attributes. */
export const ATTRIBUTE_SEPARATOR = ' · ';

/** The row's variation cells as the file has them, in column order. */
export const attributesOf = (line: ParsedLine): string =>
    (line.optionValues ?? [])
        .map(({ attributeTypeName, valueText }) => {
            const value = valueText?.trim() ?? '';
            const type = attributeTypeName?.trim() ?? '';
            if (value === '') {
                return '';
            }
            return type === '' ? value : `${type}: ${value}`;
        })
        .filter((part) => part !== '')
        .join(ATTRIBUTE_SEPARATOR);

const noteFrom = (problem: CatalogImportProblem): ImportRowNote => ({
    notice: noticeFor(problem),
    tone: toneFor(problem.code),
});

/**
 * The one thing the note column says about a row: its first problem (what
 * blocks it) over its first warning (what the import will do on its own).
 */
export const noteOf = (
    result: CatalogImportLineResult,
): ImportRowNote | null => {
    const first = result.problems[0] ?? result.warnings[0];
    return first ? noteFrom(first) : null;
};

/**
 * Everything the last check said about a row: every problem, then every
 * warning, each as a sentence with its tone. The row drawer's problem card
 * lists them; the note column shows the first (`noteOf`).
 */
export const notesOf = (result: CatalogImportLineResult): ImportRowNote[] => [
    ...result.problems.map(noteFrom),
    ...result.warnings.map(noteFrom),
];

const tetriOrNull = (value: number | null | undefined): number | null =>
    typeof value === 'number' && Number.isFinite(value)
        ? tetriFromDecimal(value)
        : null;

const numberOrNull = (value: number | null | undefined): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * The user's exclusion decides first; a row the last check left out that the
 * user did not is one included since, with no answer yet. The row drawer's
 * header reads it too.
 */
export const statusOf = (
    result: CatalogImportLineResult,
    isExcluded: boolean,
): ImportRowStatus => {
    if (isExcluded) {
        return 'excluded';
    }
    return result.status === 'excluded' ? 'unchecked' : result.status;
};

const rowOf = (
    line: ParsedLine,
    result: CatalogImportLineResult,
    isExcluded: boolean,
): ImportRow => ({
    rowNumber: result.rowNumber,
    status: statusOf(result, isExcluded),
    isExcluded,
    name: line.name?.trim() ?? '',
    sku: line.sku?.trim() ?? '',
    barcode: line.barcode?.trim() ?? '',
    lotNumber: line.lotNumber?.trim() ?? '',
    groupSize: result.groupRows.length > 1 ? result.groupRows.length : null,
    attributes: attributesOf(line),
    quantity: numberOrNull(line.quantity),
    costTetri: tetriOrNull(line.cost),
    salePriceTetri: tetriOrNull(line.salePrice),
    match: isExcluded ? null : result.match,
    note: noteOf(result),
    line,
    result,
});

/**
 * The table's rows, one per line of the preview, in its order (the request's,
 * which is the file's). Each joins the parsed line of the same row number; a
 * line the preview did not answer for has no row.
 *
 * `previous` is the last build: a row whose parsed line, answer and exclusion
 * are the same objects as before is returned as it was, so an edit or an
 * exclusion re-renders that row alone. A new preview answers every row anew.
 */
export const buildRows = (
    lines: readonly ParsedLine[],
    preview: CatalogImportResult | null,
    exclusions: ReadonlySet<number>,
    previous: readonly ImportRow[] = [],
): ImportRow[] => {
    if (!preview) {
        return [];
    }
    const lineByRow = new Map(lines.map((line) => [line.rowNumber, line]));
    const previousByRow = new Map(previous.map((row) => [row.rowNumber, row]));

    return preview.lines.flatMap((result) => {
        const line = lineByRow.get(result.rowNumber);
        if (!line) {
            return [];
        }
        const isExcluded = exclusions.has(result.rowNumber);
        const before = previousByRow.get(result.rowNumber);
        if (
            before &&
            before.line === line &&
            before.result === result &&
            before.isExcluded === isExcluded
        ) {
            return [before];
        }
        return [rowOf(line, result, isExcluded)];
    });
};

/**
 * A `buildRows` that remembers its last build, so each call reuses the rows
 * that did not change. One per table: the hook keeps it for its lifetime.
 */
export const createRowsBuilder = () => {
    let last: ImportRow[] = [];
    return (
        lines: readonly ParsedLine[],
        preview: CatalogImportResult | null,
        exclusions: ReadonlySet<number>,
    ): ImportRow[] => {
        last = buildRows(lines, preview, exclusions, last);
        return last;
    };
};
